package com.evoapps.evoshift.domain.rosterimport

import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.repository.RosterCodeRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.LocalDate

/** What the roster import works with: the month, the workplace's templates, team and saved codes. */
data class RosterImportContext(
    val roster: Roster,
    val workplaceName: String,
    val templates: List<ShiftTemplate>,
    /** The active team (the names on the sheet are matched to these). */
    val team: List<ImportTeamMember>,
    val dictionary: RosterCodeDictionary,
    /** How many shifts each day of the month already has (the Add / Replace / Skip choice). */
    val shiftsPerDay: Map<LocalDate, Int>,
    /** How many of them have someone (or record work): none means the days hold only the skeleton's unassigned shifts. */
    val assignedPerDay: Map<LocalDate, Int> = emptyMap()
)

/**
 * SH-217: everything the import screen needs, read once. A dictionary that cannot be read (never
 * saved, or refused) is empty: the codes are then asked afresh.
 */
class GetRosterImportContextUseCase(
    private val rosters: RosterRepository,
    private val workplaces: WorkplaceRepository,
    private val users: UserRepository,
    private val shifts: ShiftRepository,
    private val codes: RosterCodeRepository
) {
    suspend operator fun invoke(rosterId: String): Result<RosterImportContext> = runCatching {
        val roster = rosters.getRoster(rosterId).getOrThrow()
        val workplaceId = roster.workplaceId
        coroutineScope {
            val workplace = async { workplaces.getWorkplace(workplaceId).getOrThrow() }
            val templates = async { workplaces.getShiftTemplates(workplaceId).getOrDefault(emptyList()) }
            val team = async { users.getAssignableEmployees(workplaceId).getOrThrow() }
            val month = async { shifts.getShiftsForRoster(rosterId).getOrThrow() }
            val dictionary = async { codes.get(workplaceId).getOrDefault(RosterCodeDictionary()) }
            RosterImportContext(
                roster = roster,
                workplaceName = workplace.await().name,
                templates = templates.await(),
                team = team.await()
                    .filter { it.engagements[workplaceId]?.isActive == true }
                    .map { ImportTeamMember(it.uid, it.displayName) },
                dictionary = dictionary.await(),
                shiftsPerDay = month.await().groupingBy { LocalDate.parse(it.date.take(10)) }.eachCount(),
                assignedPerDay = month.await().filter { it.workerId != null || it.actualTime != null }
                    .groupingBy { LocalDate.parse(it.date.take(10)) }.eachCount()
            )
        }
    }
}

/**
 * SH-217: keeps the codes of an import for the next one: [used] (what this import's codes stood
 * for) over what was saved before (codes this sheet did not use stay).
 */
class SaveRosterCodesUseCase(private val codes: RosterCodeRepository) {
    suspend operator fun invoke(workplaceId: String, used: RosterCodeDictionary, editorId: String): Result<Unit> {
        if (used.codes.isEmpty()) return Result.success(Unit)
        val before = codes.get(workplaceId).getOrDefault(RosterCodeDictionary())
        val merged = before.mergedWith(used)
        // The newest codes first when there are more than the dictionary keeps.
        val kept = if (merged.codes.size <= RosterCodeDictionary.MAX_CODES) merged else
            RosterCodeDictionary((used.codes + before.codes.filterKeys { it !in used.codes }).entries.take(RosterCodeDictionary.MAX_CODES).associate { it.key to it.value })
        if (kept == before) return Result.success(Unit)
        return codes.save(workplaceId, kept, editorId)
    }
}
